import { create } from 'zustand';
import type { ProfileData } from '@/entities/profile';
import { queryClient } from '@/shared/api';
import { authService } from './api';

// Define the authentication store state
interface AuthState {
  // User data
  user: ProfileData | null;

  // Authentication status
  isLoading: boolean;
  error: string | null;

  // Сессия обязана заменить временный пароль; выставляется по профилю текущего пользователя
  // при входе и при восстановлении сессии
  isTempPassword: boolean;

  // Признак завершившейся во время работы сессии — по нему экран входа объясняет,
  // почему работа прервана и что сделанное не сохранено
  isSessionExpired: boolean;

  // Actions
  logout: () => Promise<void>;
  expireSession: () => void;
  resetError: () => void;
  setIsTempPassword: (value: boolean) => void;
}

/**
 * Состояние авторизации приложения: по нему решается, работает пользователь
 * в системе или видит экран входа.
 *
 * ## Структура
 *
 * - `user` — профиль вошедшего пользователя; `null` — работы в системе нет
 * - `isLoading` — идёт проверка сессии при открытии приложения
 * - `error` — ошибка последней попытки входа
 * - `isTempPassword` — сессия обязана заменить временный пароль постоянным
 * - `isSessionExpired` — сессия завершилась во время работы; сообщение об этом даёт экран входа
 *
 * ## Экшены
 *
 * - `logout` — выход по решению пользователя
 * - `expireSession` — завершение сессии по отказу бэка в авторизации
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  isLoading: true,
  error: null,
  isTempPassword: false,
  isSessionExpired: false,

  // Logout action — always clears local state, server call is best-effort
  logout: async () => {
    set({
      user: null,
      isLoading: false,
      error: null,
      isTempPassword: false,
      isSessionExpired: false,
    });

    queryClient.clear();
    await authService.logout();
  },

  // Завершение сессии по отказу бэка в авторизации: работа авторизованного
  // пользователя прекращается, экран входа сообщает о потере сессии.
  // Без установленной сессии — ничего не делает: так отсекаются отказ при попытке
  // входа и при проверке сессии на открытии приложения, а одновременные отказы
  // по одной и той же сессии дают одно сообщение
  expireSession: () => {
    if (get().user === null) {
      return;
    }

    set({
      user: null,
      isLoading: false,
      error: null,
      isTempPassword: false,
      isSessionExpired: true,
    });

    queryClient.clear();
  },

  // Reset error
  resetError: () => {
    set({ error: null });
  },

  setIsTempPassword: (value) => set({ isTempPassword: value }),
}));
